import { useEffect, useState } from "react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from "@/components/ui/carousel";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { SmartImage } from "@/components/SmartImage";
import { cn } from "@/lib/utils";

/**
 * Reusable product image gallery built on the shadcn Carousel.
 * - Swipeable on touch, arrow controls optional (used in the detail popup).
 * - Shows dot indicators when there is more than one image.
 * - Falls back gracefully to a single image / branded placeholder.
 */
export const ProductGallery = ({
  images = [],
  alt = "",
  fallbackLabel,
  ratio = 16 / 9,
  imageFit = "contain",
  imageClassName = "p-3",
  showArrows = false,
  onImageClick,
  className,
  testid,
}) => {
  const list = images && images.length ? images : [""];
  const single = list.length <= 1;
  const [api, setApi] = useState(null);
  const [selected, setSelected] = useState(0);

  useEffect(() => {
    if (!api) return undefined;
    const onSelect = () => setSelected(api.selectedScrollSnap());
    onSelect();
    api.on("select", onSelect);
    api.on("reInit", onSelect);
    return () => {
      api.off("select", onSelect);
    };
  }, [api]);

  const Slide = ({ src, i }) => {
    const content = (
      <AspectRatio ratio={ratio}>
        <SmartImage
          src={src}
          alt={alt ? `${alt} — larawan ${i + 1}` : ""}
          fit={imageFit}
          fallbackLabel={fallbackLabel}
          className={imageClassName}
        />
      </AspectRatio>
    );
    if (onImageClick) {
      return (
        <button
          type="button"
          onClick={onImageClick}
          className="block w-full text-left"
          aria-label={alt ? `Tingnan ang ${alt}` : "Tingnan ang produkto"}
        >
          {content}
        </button>
      );
    }
    return content;
  };

  return (
    <div className={cn("relative", className)} data-testid={testid}>
      <Carousel setApi={setApi} opts={{ loop: !single }} className="w-full">
        <CarouselContent>
          {list.map((src, i) => (
            <CarouselItem key={i}>
              <Slide src={src} i={i} />
            </CarouselItem>
          ))}
        </CarouselContent>

        {!single && showArrows && (
          <>
            <CarouselPrevious
              type="button"
              className="left-3 h-9 w-9 border-border/70 bg-background/80 text-foreground shadow-sm backdrop-blur hover:bg-background"
              data-testid={testid ? `${testid}-prev` : undefined}
            />
            <CarouselNext
              type="button"
              className="right-3 h-9 w-9 border-border/70 bg-background/80 text-foreground shadow-sm backdrop-blur hover:bg-background"
              data-testid={testid ? `${testid}-next` : undefined}
            />
          </>
        )}
      </Carousel>

      {!single && (
        <div
          className="pointer-events-none absolute inset-x-0 bottom-2.5 flex items-center justify-center gap-1.5"
          data-testid={testid ? `${testid}-dots` : undefined}
        >
          {list.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                api?.scrollTo(i);
              }}
              aria-label={`Pumunta sa larawan ${i + 1}`}
              className={cn(
                "pointer-events-auto h-1.5 rounded-full transition-all duration-200",
                selected === i ? "w-5 bg-primary" : "w-1.5 bg-foreground/25 hover:bg-foreground/40"
              )}
            />
          ))}
        </div>
      )}
    </div>
  );
};
